<script setup lang="ts">
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
const { variant = 'primary', type = 'button', disabled = false, loading = false, href } = defineProps<{
  variant?: Variant
  type?: 'button' | 'submit'
  disabled?: boolean
  loading?: boolean
  // При передаче href кнопка рендерится как Inertia Link (ссылка-кнопка) — избавляет
  // страницы от ручного дублирования primary-классов на «сырых» <Link>.
  href?: string
}>()

const classes: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-ink',
  secondary: 'bg-surface text-text border border-border-strong hover:bg-surface-2',
  ghost: 'bg-transparent text-accent hover:bg-accent-soft',
  danger: 'bg-crit-fill text-white hover:brightness-95',
}

const btnClasses = computed(() => [
  'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold',
  'transition-colors disabled:opacity-45 disabled:cursor-not-allowed',
  'aria-disabled:opacity-45 aria-disabled:cursor-not-allowed',
  classes[variant],
])

// disabled и loading гасят кнопку по-разному. loading — переходное состояние на время
// запроса: нативный disabled, потому что защита от двойного сабмита важнее, а причину
// говорят спиннер и aria-busy. disabled — состояние с причиной, которую надо услышать:
// aria-disabled, кнопка остаётся в tab-порядке и её aria-describedby доходит до
// клавиатурного пользователя.
//
// Слушатели родителя приходят через $attrs и садятся на этот же <button>, в один массив
// со здешним: stopPropagation их не остановит (они не выше по дереву, а рядом) — нужен
// stopImmediatePropagation. preventDefault отдельно нужен для type="submit": aria-disabled
// форму не останавливает.
//
// Погашенная кнопка-ссылка рендерится обычной <button>: aria-disabled на <Link> не
// остановил бы переход — Inertia вешает свой обработчик клика внутри компонента, а не
// через $attrs, и stopImmediatePropagation отсюда до него не достаёт.
function onClick(event: MouseEvent): void {
  if (!disabled) return
  event.preventDefault()
  event.stopImmediatePropagation()
}
</script>

<template>
  <Link v-if="href && !disabled" :href="href" :class="btnClasses">
    <slot />
  </Link>
  <button
    v-else
    :type="type"
    :disabled="loading || undefined"
    :aria-disabled="disabled || undefined"
    :aria-busy="loading || undefined"
    :class="btnClasses"
    @click="onClick"
  >
    <span
      v-if="loading"
      data-testid="btn-spinner"
      aria-hidden="true"
      class="h-4 w-4 rounded-full border-2 border-white/45 border-t-white motion-safe:animate-spin"
    />
    <slot />
  </button>
</template>
